import React, { useMemo } from "react";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  buildEmployeeRanking,
  buildMonthlySeries,
  rankingHeadline,
} from "./correctionRequestsChartUtils";

const FORGOTTEN_COLOR = "#ef6c00";
const CORRECTION_COLOR = "#5c7cfa";

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <Box
      sx={{
        px: 1.25,
        py: 1,
        borderRadius: 1.5,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
      }}
    >
      <Typography sx={{ fontWeight: 800, fontSize: "0.82rem", mb: 0.5 }}>
        {row.name || row.label}
      </Typography>
      <Typography
        sx={{ fontSize: "0.72rem", color: FORGOTTEN_COLOR, fontWeight: 700 }}
      >
        Olvidados: {row.forgotten}
      </Typography>
      <Typography
        sx={{ fontSize: "0.72rem", color: CORRECTION_COLOR, fontWeight: 700 }}
      >
        Correcciones: {row.corrections}
      </Typography>
      <Typography
        sx={{ fontSize: "0.72rem", color: "text.secondary", mt: 0.25 }}
      >
        Total: {row.total}
      </Typography>
    </Box>
  );
}

const CorrectionRequestsChart = ({ rows = [], year, onYearChange }) => {
  const theme = useTheme();
  const ranking = useMemo(
    () => buildEmployeeRanking(rows, { year }),
    [rows, year],
  );
  const months = useMemo(() => buildMonthlySeries(rows, year), [rows, year]);
  const headline = useMemo(() => rankingHeadline(ranking), [ranking]);
  const chartHeight = Math.min(420, Math.max(160, ranking.length * 32 + 28));

  if (!ranking.length) {
    return (
      <Box
        sx={{
          mb: 2,
          p: { xs: 1.5, sm: 2 },
          borderRadius: 2.5,
          border: "1px solid",
          borderColor: alpha(theme.palette.divider, 0.65),
          bgcolor: alpha(theme.palette.background.paper, 0.72),
        }}
      >
        <YearHeader year={year} onYearChange={onYearChange} />
        <Typography color="text.secondary" sx={{ fontWeight: 600, mt: 1 }}>
          Sin olvidos ni correcciones pendientes/aprobadas en {year}.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mb: 2,
        p: { xs: 1.5, sm: 2 },
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: alpha(theme.palette.divider, 0.65),
        bgcolor: alpha(theme.palette.background.paper, 0.72),
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 1.25 }}
      >
        <Box>
          <YearHeader year={year} onYearChange={onYearChange} />
          <Typography
            sx={{
              fontSize: "0.78rem",
              color: "text.secondary",
              fontWeight: 600,
            }}
          >
            Ranking de solicitudes (pendientes + aprobadas)
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
          <HeadlineStat label="Solicitudes" value={headline.total} />
          <HeadlineStat
            label="Olvidos"
            value={headline.forgotten}
            color={FORGOTTEN_COLOR}
          />
          {headline.top ? (
            <HeadlineStat
              label="Más solicitudes"
              value={`${headline.top.name} · ${headline.top.total}`}
            />
          ) : null}
        </Stack>
      </Stack>

      <Box sx={{ width: "100%", height: chartHeight }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={ranking}
            layout="vertical"
            margin={{ top: 4, right: 36, left: 4, bottom: 4 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              horizontal={false}
              stroke={alpha(theme.palette.divider, 0.7)}
            />
            <XAxis type="number" allowDecimals={false} hide />
            <YAxis
              type="category"
              dataKey="axisLabel"
              width={118}
              reversed
              tick={{
                fontSize: 12,
                fontWeight: 700,
                fill: theme.palette.text.primary,
              }}
            />
            <Tooltip
              content={<ChartTooltip />}
              cursor={{ fill: alpha(FORGOTTEN_COLOR, 0.06) }}
            />
            <Legend
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: 12, fontWeight: 600 }}
            />
            <Bar
              dataKey="forgotten"
              name="Fichaje olvidado"
              stackId="a"
              fill={FORGOTTEN_COLOR}
              maxBarSize={18}
            />
            <Bar
              dataKey="corrections"
              name="Corregir fichaje"
              stackId="a"
              fill={CORRECTION_COLOR}
              radius={[0, 6, 6, 0]}
              maxBarSize={18}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>

      <Typography
        sx={{
          fontSize: "0.72rem",
          fontWeight: 700,
          color: "text.secondary",
          mt: 1.5,
          mb: 0.75,
        }}
      >
        Evolución mensual {year}
      </Typography>
      <Box sx={{ width: "100%", height: 132 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={months}
            margin={{ top: 4, right: 8, left: -16, bottom: 0 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke={alpha(theme.palette.divider, 0.55)}
            />
            <XAxis
              dataKey="label"
              tick={{
                fontSize: 11,
                fontWeight: 700,
                fill: theme.palette.text.secondary,
              }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis allowDecimals={false} hide />
            <Tooltip
              content={<ChartTooltip />}
              cursor={{ fill: alpha(FORGOTTEN_COLOR, 0.06) }}
            />
            <Bar
              dataKey="forgotten"
              name="Fichaje olvidado"
              stackId="m"
              fill={FORGOTTEN_COLOR}
              maxBarSize={16}
            />
            <Bar
              dataKey="corrections"
              name="Corregir fichaje"
              stackId="m"
              fill={CORRECTION_COLOR}
              radius={[4, 4, 0, 0]}
              maxBarSize={16}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

function YearHeader({ year, onYearChange }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.25}>
      <IconButton
        size="small"
        aria-label="Año anterior"
        onClick={() => onYearChange?.(year - 1)}
        disabled={!onYearChange}
      >
        <ChevronLeftIcon fontSize="small" />
      </IconButton>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "1.05rem",
          minWidth: 48,
          textAlign: "center",
        }}
      >
        {year}
      </Typography>
      <IconButton
        size="small"
        aria-label="Año siguiente"
        onClick={() => onYearChange?.(year + 1)}
        disabled={!onYearChange}
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>
      <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", pl: 0.5 }}>
        Olvidos de fichaje
      </Typography>
    </Stack>
  );
}

function HeadlineStat({ label, value, color }) {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: "0.62rem",
          fontWeight: 800,
          letterSpacing: 0.4,
          textTransform: "uppercase",
          color: "text.secondary",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.95rem",
          color: color || "text.primary",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default CorrectionRequestsChart;
